import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import { Notification } from "@rio-cloud/rio-uikit";
import type { TransactionImportType } from "@types";
import { useT } from "@hooks";
import { getTransactionIds } from "@services/form";
import type { SharedTableSelection, SharedVehicleRow } from "@components/Table/SharedTable";
import type { SelectionBarProps } from "@components/Table/SelectionBar";
import { toBackendStatus } from "@utils/transactionRecurrence";
import { parseCurrencyBRL } from "@utils";
import type { DashboardFilter } from "../../types/dashboard";
import { resolveMomentLocale } from "./transactionScreenUtils";
import { useBatchSelection, type BatchSelectionItem } from "./useBatchSelection";
import type { TransactionListOriginFilters } from "./useTransactionListData";

type Row = SharedVehicleRow<string>;

interface Params {
  type: TransactionImportType;
  rows: Row[];
  totalRecords: number;
  startDate: string;
  endDate: string;
  search: string;
  listFilters: DashboardFilter;
  originFilters: TransactionListOriginFilters;
  onDeleted: () => void;
  canBatchDelete: boolean;
}

const isRowDeletable = (row: Pick<Row, "deletable">) => row.deletable !== false;

export const toSelectionItem = (row: Row): BatchSelectionItem => ({
  id: row.id,
  version: row.version ?? null,
  recurrenceId: row.recurrenceId ?? null,
  status: toBackendStatus(row.status) ?? "FORECASTED",
  amount: row.amountValue ?? parseCurrencyBRL(row.amount),
  date: row.date,
  category: row.category,
  subcategory: row.subcategory,
  totalInstallments: row.totalInstallments ?? null,
  deletable: isRowDeletable(row),
});

const DEFAULT_NOT_DELETABLE_TOOLTIP = "batchDelete.selection.notDeletable";

const NOT_DELETABLE_TOOLTIP_BY_SOURCE: Record<string, string> = {
  MARKETPLACE: "batchDelete.selection.notDeletable.marketplace",
  TRUCKER_ASSIST: "batchDelete.selection.notDeletable.route",
  TRUCKERTAG: "batchDelete.selection.notDeletable.allFleet",
};

export const resolveNotDeletableTooltipKey = (source?: string | null) =>
  NOT_DELETABLE_TOOLTIP_BY_SOURCE[
    String(source ?? "")
      .trim()
      .toUpperCase()
  ] ?? DEFAULT_NOT_DELETABLE_TOOLTIP;

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const useTransactionBatchDelete = ({
  type,
  rows,
  totalRecords,
  startDate,
  endDate,
  search,
  listFilters,
  originFilters,
  onDeleted,
  canBatchDelete,
}: Params) => {
  const t = useT();
  const { locale } = useIntl();
  const [showDialog, setShowDialog] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const resetKey = useMemo(
    () => JSON.stringify({ startDate, endDate, search, listFilters, originFilters }),
    [startDate, endDate, search, listFilters, originFilters]
  );

  const selection = useBatchSelection(resetKey);
  const { isSelected, toggle, setMany, replaceWithFilter, clear } = selection;

  const isFilterSelection = selection.source === "filter";
  const rowsById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);
  const headerRows = useMemo(() => (isFilterSelection ? rows.filter(isRowDeletable) : rows), [isFilterSelection, rows]);
  const selectedLoadedCount = headerRows.filter((row) => isSelected(row.id)).length;

  const headerState = useMemo(() => {
    if (selectedLoadedCount === 0) return "none" as const;
    return selectedLoadedCount === headerRows.length ? ("all" as const) : ("some" as const);
  }, [headerRows.length, selectedLoadedCount]);

  const deletableItems = useMemo(
    () => selection.selectedItems.filter((item) => item.deletable !== false),
    [selection.selectedItems]
  );

  const handleToggle = (row: Row) => {
    if (!canBatchDelete && selection.count > 0 && !isSelected(row.id)) {
      Notification.info(t("batchDelete.selection.singleOnly"));
      return;
    }
    toggle(toSelectionItem(rowsById.get(row.id) ?? row));
  };

  const tableSelection: SharedTableSelection<Row> = {
    isSelected: (row) => isSelected(row.id),
    isNotDeletable: (row) => !isRowDeletable(row),
    onToggle: handleToggle,
    headerState,
    headerDisabled: rows.length === 0,
    headerHidden: !canBatchDelete,
    onToggleAll: () => setMany(rows.map(toSelectionItem), headerState !== "all"),
    getNotDeletableTooltip: (row) => t(resolveNotDeletableTooltipKey(row.integrationOrigin ?? row.source)),
    notDeletableTooltipTitle: t("batchDelete.selection.notDeletable.title"),
    lockNotDeletable: showDialog,
  };

  const selectAllFromFilter = useCallback(async () => {
    setIsSelectingAll(true);
    try {
      const result = await getTransactionIds(
        startDate,
        endDate,
        type,
        { searchTerm: search, origins: originFilters.origins, importIds: originFilters.importIds },
        listFilters
      );

      if (result.limitExceeded) {
        Notification.warning(t("batchDelete.error.limit"));
        return;
      }

      if (result.items.length === 0) {
        Notification.info(t("batchDelete.selection.noneDeletable"));
        return;
      }

      replaceWithFilter(
        result.items.map((item) => {
          const row = rowsById.get(item.id);
          return {
            ...item,
            date: row?.date ?? null,
            category: row?.category ?? null,
            subcategory: row?.subcategory ?? null,
            totalInstallments: row?.totalInstallments ?? null,
          };
        })
      );
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      Notification.error(detail || t("batchDelete.error.generic"));
    } finally {
      setIsSelectingAll(false);
    }
  }, [endDate, listFilters, originFilters, replaceWithFilter, rowsById, search, startDate, t, type]);

  const hasMoreThanLoaded = totalRecords > rows.length;

  const selectionBar: SelectionBarProps = {
    count: selection.count,
    totalAmount: selection.totalAmount,
    isFilterSelection,
    selectAllFromFilterTotal: canBatchDelete && headerState === "all" && hasMoreThanLoaded ? totalRecords : null,
    isSelectingAllFromFilter: isSelectingAll,
    onSelectAllFromFilter: selectAllFromFilter,
    onClear: clear,
    onDelete: () => {
      if (deletableItems.length === 0) {
        Notification.info(t("batchDelete.selection.onlyIntegration"));
        return;
      }
      setShowDialog(true);
    },
  };

  const periodLabel = useMemo(() => {
    const start = moment(startDate, "YYYY-MM-DD").locale(resolveMomentLocale(locale));
    const end = moment(endDate, "YYYY-MM-DD");

    if (start.isSame(end, "month")) {
      return t("batchDelete.confirm.period.month", {
        month: capitalize(start.format("MMMM")),
        year: start.format("YYYY"),
      });
    }

    return t("batchDelete.confirm.period.range", {
      start: start.format("DD/MM/YYYY"),
      end: end.format("DD/MM/YYYY"),
    });
  }, [endDate, locale, startDate, t]);

  const dialog = {
    show: showDialog,
    module: type,
    items: deletableItems,
    isFilterSelection,
    periodLabel,
    integrationCount: selection.count - deletableItems.length,
    onCancel: () => {
      setShowDialog(false);
      clear();
    },
    onFinished: ({ keepSelection }: { keepSelection: boolean }) => {
      setShowDialog(false);
      if (!keepSelection) clear();
      onDeleted();
    },
  };

  return {
    tableSelection,
    selectionBar,
    dialog,
  };
};
